import React, {useCallback} from "react";
import {ASkeletonHeader} from "@cisco-sbg-ui/magna-react";
import {t} from "i18next";
import {useSelector} from "react-redux";
import {EntityDrawer} from "features/drawers";
import AssetsCard from "features/incidentOverview/AssetsCard";
import AttackGraph from "features/incidentOverview/AttackGraph/AttackGraph";
import IndicatorsCard from "features/incidentOverview/IndicatorsCard";
import ObservablesCard from "features/incidentOverview/ObservablesCard";
import {Card} from "components/Card";
import useDrawer from "hooks/useDrawer";
import {
  selectAssetsCount,
  selectAssetsData,
  selectIncidentsOverviewLoading,
  selectIndicatorsCount,
  selectIndicatorsData,
  selectObservablesCount,
  selectObservablesData
} from "store/selectors/incidentOverviewSelectors";
import {sendIncidentOverviewPanelViewLinksTelemetry} from "api/telemetry/incidentsTelemetrics";
import {FieldNames} from "models/fields";
import CardTitleCount from "./CardTitleCount";
import css from "./IncidentDetailOverview.module.scss";

const IncidentDetailOverview = () => {
  const assetsData = useSelector(selectAssetsData);
  const assetCount = useSelector(selectAssetsCount);
  const observablesData = useSelector(selectObservablesData);
  const observablesCount = useSelector(selectObservablesCount);
  const indicatorsCount = useSelector(selectIndicatorsCount);
  const indicatorsData = useSelector(selectIndicatorsData);
  const isOverviewLoading = useSelector(selectIncidentsOverviewLoading);

  const {
    close: closeDrawer,
    open: openDrawer,
    isOpen: isDrawerOpen,
    data: selectedEntityType
  } = useDrawer();

  const handleCardSelection = useCallback(
    (fieldName, count) => () => {
      sendIncidentOverviewPanelViewLinksTelemetry(fieldName, count);
      openDrawer(fieldName);
    },
    [openDrawer]
  );

  const overviewCards = {
    [FieldNames.assets]: {
      count: assetCount,
      data: assetsData,
      content: <AssetsCard />,
      action:
        assetCount && !isOverviewLoading ? (
          <a onClick={handleCardSelection(FieldNames.assets, assetCount)}>
            {t("overview.links.viewAssets")}
          </a>
        ) : null,
      title: isOverviewLoading ? (
        <ASkeletonHeader />
      ) : (
        <CardTitleCount i18nKey="overview.title.assets" count={assetCount} />
      ),
      ariaLabel: t("drawers.labels.common.assetsCount", {
        count: assetCount
      })
    },
    [FieldNames.observables]: {
      count: observablesCount,
      data: observablesData,
      content: <ObservablesCard />,
      action:
        observablesCount && !isOverviewLoading ? (
          <a
            onClick={handleCardSelection(
              FieldNames.observables,
              observablesCount
            )}>
            {t("overview.links.viewObservables")}
          </a>
        ) : null,
      title: isOverviewLoading ? (
        <ASkeletonHeader />
      ) : (
        <CardTitleCount
          i18nKey="overview.title.observables"
          count={observablesCount}
        />
      ),
      ariaLabel: t("drawers.labels.common.observablesCount", {
        count: observablesCount
      })
    },
    [FieldNames.indicators]: {
      count: indicatorsCount,
      data: indicatorsData,
      content: <IndicatorsCard />,
      action:
        indicatorsCount && !isOverviewLoading ? (
          <a
            onClick={handleCardSelection(
              FieldNames.indicators,
              indicatorsCount
            )}>
            {t("overview.links.viewIndicators")}
          </a>
        ) : null,
      title: isOverviewLoading ? (
        <ASkeletonHeader />
      ) : (
        <CardTitleCount
          i18nKey="overview.title.indicators"
          count={indicatorsCount}
        />
      ),
      ariaLabel: t("drawers.labels.common.indicatorsCount", {
        count: indicatorsCount
      })
    }
  };

  return (
    <div
      className="tab-content tab-content__overview"
      data-testid={"IncidentDetailOverview-container"}>
      <div className={css.overviewGrid}>
        <AttackGraph />
        {Object.entries(overviewCards).map(
          ([key, {content, ...cardProps}], i) => (
            <Card key={`${key}-${i}`} className={css.card} {...cardProps}>
              {content}
            </Card>
          )
        )}
      </div>
      <EntityDrawer
        count={overviewCards[selectedEntityType]?.count}
        data={overviewCards[selectedEntityType]?.data}
        entityType={selectedEntityType}
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
      />
    </div>
  );
};

export default IncidentDetailOverview;
